"use client";

import { useState } from "react";
import {
  CopyButton,
  Notice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  filterStarters,
  SENTENCE_STARTERS,
  STARTER_FUNCTIONS,
  type StarterFunction,
  type StarterGroup,
  type StarterPart,
  starterText,
} from "@/lib/tool-kit/essay/sentence-starters";

/** English strings. A translated content file overrides these through `ui`. */
export const SENTENCE_STARTER_GENERATOR_STRINGS = {
  topicLabel: "Your topic (optional)",
  topicPlaceholder: "e.g. the four-day school week",
  topicHint:
    "Fills the topic slot in the starters that have one. Leave it empty to see the slot marked.",
  functionLabel: "What should the sentence do?",
  allFunctions: "All functions",
  searchLabel: "Search the starters",
  searchPlaceholder: "e.g. evidence, contrast, results",
  tryExample: "Try an example",
  clear: "Clear",
  exampleTopic: "the four-day school week",
  topicStandIn: "[topic]",
  showing: "one: Showing {count} starter|other: Showing {count} starters",
  copyShown: "Copy all shown",
  copy: "Copy",
  copied: "Copied!",
  noMatches:
    "No starters match “{query}”. Try a shorter word, or choose All functions.",
  legend:
    "___ marks a blank to fill in; … means the sentence continues in your own words.",
  howTo:
    "Starters are a scaffold, not a script — finish each one with your own ideas and evidence, and vary them so no two paragraphs open the same way.",
  functions: {
    introducing: "Introducing a topic",
    background: "Giving background",
    thesis: "Stating a thesis",
    evidence: "Adding evidence",
    explaining: "Explaining and analyzing",
    comparing: "Comparing and contrasting",
    conceding: "Conceding a point",
    emphasizing: "Emphasizing a point",
    methods: "Describing methods",
    results: "Reporting results",
    limitations: "Discussing limitations",
    concluding: "Concluding",
  },
  functionHints: {
    introducing: "Opening sentences that name the subject and why it matters.",
    background: "Context, history, and definitions the reader needs first.",
    thesis: "Sentences that commit you to a central claim.",
    evidence: "Ways to bring in examples, data, and sources.",
    explaining: "Sentences that say what your evidence means.",
    comparing: "Similarities and differences between ideas, texts, or results.",
    conceding: "Acknowledging other views and the limits of your own.",
    emphasizing: "Signaling which point matters most.",
    methods: "How a study, survey, or investigation was carried out.",
    results: "What the data or analysis showed.",
    limitations: "What the evidence cannot show, and what could come next.",
    concluding: "Pulling the argument together at the end.",
  },
  starters: SENTENCE_STARTERS,
} as const;

type Strings = typeof SENTENCE_STARTER_GENERATOR_STRINGS;

type FunctionChoice = StarterFunction | "all";

function Starter({ parts }: { parts: readonly StarterPart[] }) {
  return (
    <span className="text-foreground text-sm leading-relaxed">
      {parts.map((part, index) =>
        part.slot ? (
          // Parts are positional pieces of one sentence.
          // biome-ignore lint/suspicious/noArrayIndexKey: positional parts
          <span className="font-medium text-primary" key={index}>
            {part.text}
          </span>
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: positional parts
          <span key={index}>{part.text}</span>
        )
      )}
    </span>
  );
}

function shownText(groups: readonly StarterGroup[], strings: Strings): string {
  return groups
    .map((group) =>
      [
        strings.functions[group.fn],
        ...group.starters.map((parts) => starterText(parts)),
      ].join("\n")
    )
    .join("\n\n");
}

export default function SentenceStarterGeneratorWidget() {
  const strings = useToolUi(SENTENCE_STARTER_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [topic, setTopic] = useState("");
  const [fn, setFn] = useState<FunctionChoice>("all");
  const [query, setQuery] = useState("");

  const groups = filterStarters(
    strings.starters,
    fn,
    query,
    topic,
    strings.topicStandIn
  );
  const shown = groups.reduce((sum, group) => sum + group.starters.length, 0);

  const options = [
    { value: "all" as const, label: strings.allFunctions },
    ...STARTER_FUNCTIONS.map((value) => ({
      value,
      label: strings.functions[value],
    })),
  ];

  return (
    <>
      <div className={`${CARD} space-y-4`}>
        <TextField
          hint={strings.topicHint}
          id="ssg-topic"
          label={strings.topicLabel}
          onChange={setTopic}
          placeholder={strings.topicPlaceholder}
          value={topic}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField<FunctionChoice>
            id="ssg-function"
            label={strings.functionLabel}
            onChange={setFn}
            options={options}
            value={fn}
          />
          <TextField
            id="ssg-search"
            label={strings.searchLabel}
            onChange={setQuery}
            placeholder={strings.searchPlaceholder}
            type="search"
            value={query}
          />
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setTopic(strings.exampleTopic)}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => {
              setTopic("");
              setFn("all");
              setQuery("");
            }}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </div>

      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* The count is the live part: re-announcing two hundred starters on every
              keystroke would drown a screen reader. */}
          <h2 aria-live="polite" className={WIDGET_H2}>
            {tPlural(strings.showing, locale, shown, { count: fmt(shown) })}
          </h2>
          <CopyButton
            copiedLabel={strings.copied}
            disabled={shown === 0}
            label={strings.copyShown}
            small
            text={shownText(groups, strings)}
          />
        </div>
        <p className={HINT}>{strings.legend}</p>
        {shown === 0 ? (
          <Notice>{t(strings.noMatches, { query: query.trim() })}</Notice>
        ) : (
          groups.map((group) => (
            <div className={`${CARD} space-y-3`} key={group.fn}>
              <div>
                <h3 className={WIDGET_H3}>{strings.functions[group.fn]}</h3>
                <p className={HINT}>{strings.functionHints[group.fn]}</p>
              </div>
              <ul className="divide-y divide-border">
                {group.starters.map((parts) => {
                  const text = starterText(parts);
                  return (
                    <li
                      className="flex items-start justify-between gap-3 py-2"
                      key={text}
                    >
                      <Starter parts={parts} />
                      <CopyButton
                        copiedLabel={strings.copied}
                        label={strings.copy}
                        small
                        text={text}
                      />
                    </li>
                  );
                })}
              </ul>
            </div>
          ))
        )}
        <p className="text-muted-foreground text-sm">{strings.howTo}</p>
      </section>
    </>
  );
}
